import React, { useCallback, useEffect, useState } from "react";
import { Bell, Send, Users, Zap, Trash2 } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

function AdminNotifications() {
  const [form, setForm] = useState({ title: "", message: "", audience_mode: "all", audience: "all", topic: "all", user_id: "" });
  const [stats, setStats] = useState({ sent_today: 0, total_users: 0 });
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [notificationRes, usersRes] = await Promise.all([
        API.get("/admin/notifications"),
        API.get("/admin/users"),
      ]);
      setStats(notificationRes.data?.stats || {});
      setItems(Array.isArray(notificationRes.data?.notifications) ? notificationRes.data.notifications : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch {
      setStats({ sent_today: 0, total_users: 0 });
      setItems([]);
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sendNotification = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.message.trim()) return;
    const payload = {
      title: form.title,
      message: form.message,
      topic: form.topic || "all",
      audience:
        form.audience_mode === "user"
          ? String(form.user_id || "").trim()
          : form.audience_mode === "topic"
            ? "all"
            : "all",
    };
    if (form.audience_mode === "user" && !payload.audience) {
      alert("Select a user first");
      return;
    }
    setSaving(true);
    try {
      await API.post("/admin/notifications", payload);
      setForm({ title: "", message: "", audience_mode: "all", audience: "all", topic: "all", user_id: "" });
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const deleteNotification = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    try {
      await API.delete(`/admin/notifications/${id}`);
      await loadData();
    } catch (err) {
      alert("Failed to delete notification");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div style={{ borderLeft: "4px solid #4f46e5", paddingLeft: "14px" }}>
            <h1 style={title}>Send Push Notification</h1>
            <p style={subtitle}>Send in-app notifications to all users</p>
          </div>
          <div style={connected}><Zap size={14} /> In-App Connected</div>
        </div>

        <div style={grid}>
          <form style={card} onSubmit={sendNotification}>
            <label style={label}>Notification Title</label>
            <input
              style={input}
              value={form.title}
              maxLength={100}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Result Declared!"
            />
            <span style={counter}>{form.title.length}/100 characters</span>

            <label style={label}>Message</label>
            <textarea
              style={textarea}
              value={form.message}
              maxLength={500}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Write your notification message here..."
            />
            <span style={counter}>{form.message.length}/500 characters</span>

            <label style={label}>Target Audience</label>
            <div style={audienceGrid}>
              <button type="button" style={form.audience_mode === "all" ? audienceActive : audienceButton} onClick={() => setForm((prev) => ({ ...prev, audience_mode: "all", audience: "all", topic: "all" }))}>
                <Users size={18} />
                <span><b>All Users</b><small style={{ display: "block", fontWeight: 400, marginTop: 2, opacity: 0.75 }}>Broadcast to everyone</small></span>
              </button>
              <button type="button" style={form.audience_mode === "topic" ? audienceActive : audienceButton} onClick={() => setForm((prev) => ({ ...prev, audience_mode: "topic", audience: "all", topic: "results" }))}>
                <Bell size={18} />
                <span><b>Result Topic</b><small style={{ display: "block", fontWeight: 400, marginTop: 2, opacity: 0.75 }}>Subscribed to results</small></span>
              </button>
              <button type="button" style={form.audience_mode === "user" ? audienceActive : audienceButton} onClick={() => setForm((prev) => ({ ...prev, audience_mode: "user", audience: "", user_id: prev.user_id || "" }))}>
                <Zap size={18} />
                <span><b>Single User</b><small style={{ display: "block", fontWeight: 400, marginTop: 2, opacity: 0.75 }}>Send to one player</small></span>
              </button>
            </div>

            {form.audience_mode === "user" && (
              <>
                <label style={label}>Select User</label>
                <select
                  style={input}
                  value={form.user_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, user_id: e.target.value, audience: e.target.value }))}
                >
                  <option value="">Choose user</option>
                  {users.slice(0, 1000).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || `User ${user.id}`} - {user.phone || user.id}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label style={label}>Preview</label>
            <div style={previewShell}>
              <div style={previewHeader}>
                <div style={previewDot} />
                <div style={previewDot} />
                <div style={previewDot} />
              </div>
              <div style={previewCard}>
                <span style={previewIcon}><Bell size={18} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ display: "block", fontSize: 13, color: "#0f172a" }}>{form.title || "Notification Title"}</strong>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis" }}>{form.message || "Your notification message will appear here..."}</p>
                  <small style={{ display: "block", marginTop: 6, fontSize: 11, color: "#94a3b8" }}>
                    {form.audience_mode === "user"
                      ? `User ${form.user_id || "-"} · Play Online Satta Matka`
                      : form.audience_mode === "topic"
                        ? "Result Topic · Play Online Satta Matka"
                        : "All Users · Play Online Satta Matka"}
                  </small>
                </div>
              </div>
            </div>

            <button type="submit" style={sendButton} disabled={saving || !form.title.trim() || !form.message.trim()}>
              <Send size={18} />
              {saving ? "Sending..." : form.audience_mode === "user" ? "Send to User" : form.audience_mode === "topic" ? "Send to Result Topic" : "Send to All Users"}
            </button>
          </form>

          <aside style={side}>
            <article style={statCard}>
              <div style={statCardHeader}><Zap size={16} color="#f59e0b" /> <strong style={{ fontSize: 14, color: "#1e1b4b", fontWeight: 900 }}>Statistics</strong></div>
              <div style={statRow}><span>Sent Today</span><b style={{ color: "#4f46e5", fontWeight: 900 }}>{Number(stats.sent_today || 0)}</b></div>
              <div style={statRow}><span>Total Users</span><b style={{ color: "#16a34a", fontWeight: 900 }}>{Number(stats.total_users || 0).toLocaleString("en-IN")}</b></div>
            </article>
            <article style={infoCard}>
              <div style={statCardHeader}><Bell size={16} color="#4f46e5" /> <strong style={{ fontSize: 14, color: "#1e1b4b", fontWeight: 900 }}>Recent Notifications</strong></div>
              {items.slice(0, 6).map((item) => (
                <div key={item.id} style={itemRow}>
                  <div style={itemContent}>
                    <b style={{ color: "#1e1b4b", fontSize: 13 }}>{item.title}</b>
                    <span style={{ color: "#64748b" }}>{item.message}</span>
                  </div>
                  <button type="button" style={deleteButton} onClick={() => deleteNotification(item.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {!items.length && <p style={empty}>No notifications sent yet</p>}
            </article>
          </aside>
        </div>
      </section>
    </AdminLayout>
  );
}

const page = { display: "grid", gap: 20 };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" };
const title = { margin: 0, fontSize: 22, fontWeight: 900, color: "#1e1b4b" };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: 13 };
const connected = { height: 38, padding: "0 14px", border: "1.5px solid #86efac", background: "#f0fdf4", color: "#15803d", display: "flex", alignItems: "center", gap: 8, fontWeight: 800, borderRadius: 12 };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 18, maxWidth: 900, margin: "0 auto", width: "100%" };
const card = { background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 16, padding: 20, display: "grid", gap: 10, boxShadow: "0 4px 20px rgba(79,70,229,0.08)" };
const label = { fontSize: 13, fontWeight: 800, color: "#1e1b4b", marginTop: 6 };
const input = { height: 46, border: "1.5px solid #e0e7ff", borderRadius: 12, padding: "0 14px", outline: 0, fontSize: 14 };
const textarea = { minHeight: 96, border: "1.5px solid #e0e7ff", borderRadius: 12, padding: 14, outline: 0, resize: "vertical", fontSize: 14, fontFamily: "inherit" };
const counter = { fontSize: 11, color: "#94a3b8" };
const audienceGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 };
const audienceButton = { border: "1.5px solid #e0e7ff", background: "#fff", minHeight: 68, borderRadius: 12, padding: "0 14px", display: "flex", alignItems: "center", gap: 10, textAlign: "left", color: "#334155", cursor: "pointer", fontWeight: 700 };
const audienceActive = { ...audienceButton, borderColor: "#4f46e5", background: "linear-gradient(135deg, #ede9fe, #ddd6fe)", color: "#4f46e5" };
const previewShell = { background: "#1e1b4b", padding: "12px 14px 14px", borderRadius: 16 };
const previewHeader = { display: "flex", gap: 6, marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.1)" };
const previewDot = { width: 8, height: 8, borderRadius: "50%", background: "rgba(255,255,255,0.3)" };
const previewCard = { background: "#fff", padding: "12px 14px", borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 12 };
const previewIcon = { width: 38, height: 38, background: "linear-gradient(135deg, #4f46e5, #312e81)", color: "#fff", display: "grid", placeItems: "center", borderRadius: 10, flexShrink: 0 };
const sendButton = { height: 52, border: 0, borderRadius: 12, background: "linear-gradient(135deg, #4f46e5, #312e81)", color: "#fff", fontSize: 15, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", boxShadow: "0 4px 14px rgba(79,70,229,0.30)", marginTop: 4 };
const side = { display: "grid", gap: 16, alignContent: "start" };
const statCard = { background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 16, padding: 18, display: "grid", gap: 10, boxShadow: "0 4px 20px rgba(79,70,229,0.08)" };
const statCardHeader = { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 };
const infoCard = { ...statCard, background: "#fafafe", borderColor: "#e0e7ff" };
const statRow = { height: 36, display: "flex", alignItems: "center", justifyContent: "space-between", color: "#475569", fontSize: 14, borderBottom: "1px solid #f1f5f9" };
const itemRow = { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #f1f5f9" };
const itemContent = { flex: 1, display: "grid", gap: 3, fontSize: 12, minWidth: 0 };
const deleteButton = { width: 34, height: 34, border: 0, background: "#fef2f2", color: "#dc2626", borderRadius: 10, display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 };
const empty = { margin: 0, color: "#94a3b8", fontSize: 13 };

export default AdminNotifications;
